/**
 * SocketManager - Socket.io Connection Lifecycle Manager
 *
 * Handles socket connection, disconnection, and coordinates event registration.
 * Provides a clean interface for the game client to interact with the server.
 *
 * Responsibilities:
 * - Socket connection lifecycle (connect, disconnect, reconnect)
 * - Event handler registration coordination
 * - Emit wrapper methods for common actions
 * - Connection state tracking
 */

import { io } from 'socket.io-client';
import { parseNetSim, installNetSim } from './NetSim.js';
import { Protocol } from '../../shared/protocol.js';
import { ClientLogger } from '../logger/ClientLogger.js';

const socketLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const socketLogger = ClientLogger.getLogger('network') || socketLoggerFallback;

class SocketManager {
  /**
   * The player's identity token, stable for the lifetime of this browser tab.
   *
   * sessionStorage is per tab, so two tabs on one machine stay two distinct
   * players (the usual local-testing setup) while a reload or a transport drop
   * in one tab keeps its identity and can rejoin a live match.
   * @returns {string}
   */
  static loadSessionToken() {
    const KEY = 'driftshot.sessionToken';
    try {
      let token = sessionStorage.getItem(KEY);
      if (!token) {
        token = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
        sessionStorage.setItem(KEY, token);
      }
      return token;
    } catch (e) {
      return `${Date.now()}-${Math.random()}`; // storage blocked: token lives for this page load only
    }
  }

  /**
   * Create a new SocketManager
   * @param {GameState} gameState - The game state container
   * @param {Object} options - Socket.io options
   */
  constructor(gameState, options = {}) {
    this.gameState = gameState;

    // Create socket with default options
    this.socket = io({
      autoConnect: false,
      transports: ['websocket', 'polling'],
      ...options
    });

    this.netSim = parseNetSim(location.search, location.hostname);
    if (this.netSim) {
      installNetSim(this.socket, this.netSim);
      socketLogger.warn('NetSim active', this.netSim);
    }
    this.socket.on('netProbe', (ack) => {
      if (typeof ack !== 'function') return;
      if (this.socket.netSimDelayOutbound) this.socket.netSimDelayOutbound(ack);
      else ack();
    });

    // Track connection state
    this.connected = false;
    this._suppressNextReconnectNotification = false;

    // Validate outgoing payloads against the protocol table when developing
    // locally, so schema mismatches fail loudly instead of being dropped server-side.
    this.devValidation = location.hostname === 'localhost' || location.hostname === '127.0.0.1';

    // Stable per-tab identity, so a socket drop or a reload can rejoin a match
    this.sessionToken = SocketManager.loadSessionToken();

    // Event modules will be registered here
    this.eventModules = [];

    // Setup connection event handlers
    this._setupConnectionHandlers();
  }

  /**
   * Setup connection lifecycle event handlers
   * @private
   */
  _setupConnectionHandlers() {
    // Track if this is a reconnection
    let hasConnectedBefore = false;

    this.socket.on('connect', () => {
      this.connected = true;
      const isReconnect = hasConnectedBefore;
      hasConnectedBefore = true;

      socketLogger.info(isReconnect ? 'Socket reconnected' : 'Socket connected', '| socket.id:', this.socket.id);

      // Call reconnect callback if this is a reconnection
      if (isReconnect && this.onReconnect) {
        if (this._suppressNextReconnectNotification) {
          this._suppressNextReconnectNotification = false;
        } else {
          this.onReconnect();
        }
      } else if (!isReconnect && this.onConnect) {
        this.onConnect();
      }
    });

    this.socket.on('disconnect', (reason) => {
      this.connected = false;
      socketLogger.warn('Socket disconnected:', reason);

      if (this.onDisconnect) {
        this.onDisconnect(reason);
      }
    });

    this.socket.on('connect_error', (error) => {
      socketLogger.error('Socket connection error:', error.message);
    });

    this.socket.on('protocolMismatch', (info) => {
      socketLogger.error('Protocol version mismatch', info);
      if (this.onProtocolMismatch) this.onProtocolMismatch(info);
    });
  }

  /**
   * Set callback for a server-reported protocol version mismatch
   * @param {Function} callback
   */
  setOnProtocolMismatch(callback) {
    this.onProtocolMismatch = callback;
  }

  /**
   * Set callback for initial connection
   * @param {Function} callback
   */
  setOnConnect(callback) {
    this.onConnect = callback;
  }

  /**
   * Set callback for reconnection
   * @param {Function} callback
   */
  setOnReconnect(callback) {
    this.onReconnect = callback;
  }

  /**
   * Suppress the next reconnect callback so we can avoid spurious messages after an intentional disconnect.
   */
  suppressReconnectNotificationOnce() {
    this._suppressNextReconnectNotification = true;
  }

  /**
   * Set callback for disconnection
   * @param {Function} callback
   */
  setOnDisconnect(callback) {
    this.onDisconnect = callback;
  }

  /**
   * Register an event module
   * Event modules should have a register(socket, gameState, callbacks) method
   * @param {Object} eventModule - The event module to register
   * @param {Object} callbacks - UI/system callbacks the module can use
   */
  registerEventModule(eventModule, callbacks = {}) {
    if (typeof eventModule.register === 'function') {
      eventModule.register(this.socket, this.gameState, callbacks);
      this.eventModules.push(eventModule);
    } else {
      socketLogger.warn('Event module missing register method:', eventModule);
    }
  }

  /**
   * Connect to the server
   */
  connect() {
    if (!this.connected) {
      this.socket.connect();
    }
  }

  /**
   * Disconnect from the server
   */
  disconnect() {
    if (this.connected) {
      this.socket.disconnect();
    }
  }

  /**
   * Check if socket is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.connected;
  }

  /**
   * Get the underlying socket for direct access if needed
   * @returns {Socket}
   */
  getSocket() {
    return this.socket;
  }

  // ─────────────────────────────────────────────────────────────
  // CLIENT → SERVER MESSAGES
  // ─────────────────────────────────────────────────────────────

  /**
   * The single path for client→server messages. In development the payload is
   * validated against shared/protocol.js so a client bug surfaces here, not as
   * a silently dropped packet on the server.
   * @param {string} name - a key of Protocol.ClientMessages
   * @param {Object} [payload]
   * @returns {boolean} whether the message was emitted
   */
  send(name, payload = {}) {
    if (typeof Protocol !== 'undefined') {
      const def = Protocol.ClientMessages[name];
      if (!def) {
        socketLogger.error(`send(): '${name}' is not in the protocol table`);
        return false;
      }
      if (this.devValidation) {
        const res = Protocol.validateClientMessage(name, payload);
        if (!res.ok) {
          socketLogger.error(`send('${name}') rejected by protocol validation: ${res.error}`, payload);
          return false;
        }
      }
    }
    this.socket.emit(name, payload);
    return true;
  }

  /**
   * Register a one-time event listener
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  once(event, handler) {
    this.socket.once(event, handler);
  }

  /**
   * Register an event listener
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  on(event, handler) {
    this.socket.on(event, handler);
  }

  /**
   * Remove an event listener
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  off(event, handler) {
    this.socket.off(event, handler);
  }

  // ─────────────────────────────────────────────────────────────
  // PING TRACKING
  // ─────────────────────────────────────────────────────────────

  /**
   * Start ping tracking with periodic measurements
   * @param {Function} onPingUpdate - Callback with ping value in ms
   * @param {number} interval - Ping interval in ms (default 2000)
   */
  /** Negotiated Engine.IO transport ('websocket' | 'polling'), for the netcode overlay. */
  transportName() {
    return this.socket?.io?.engine?.transport?.name ?? null;
  }

  startPingTracking(onPingUpdate, interval = 2000) {
    this.pingCallback = onPingUpdate;
    this.currentPing = 0;

    // Listen for pong response
    this.socket.on('pong_response', (data) => {
      const rtt = performance.now() - data.clientTime;
      this.currentPing = Math.round(rtt);
      if (this.pingCallback) {
        this.pingCallback(this.currentPing);
      }
    });

    // Send periodic pings
    this.pingInterval = setInterval(() => {
      if (this.connected) {
        this.send('ping_request', { clientTime: performance.now() });
      }
    }, interval);
  }

  /**
   * Stop ping tracking
   */
  stopPingTracking() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    this.socket.off('pong_response');
  }

  /**
   * Get current ping value
   * @returns {number} Current ping in ms
   */
  getPing() {
    return this.currentPing || 0;
  }
}

// Export for use in other modules
// For now, SocketManager is a global class loaded via script tag

export { socketLoggerFallback, socketLogger, SocketManager };
